import { useState, useEffect } from 'react';

import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Tabs, Tab } from '@mui/material';

import { bannerHeader, bannerHeaderBox, bannerMain, content } from './styles';
import Banner from '../../assets/MainPage/banner.png';
import useCurrentUser from '../../hooks/useCurrentUser';
import { CURRENT_USER_ROLE } from '../../hooks/useLogin';
import PageLayout from 'src/components/Layout/PageLayout';
import { getMyCourse, getMyMission, getMyResult } from 'src/api/apis';
import { TokenExpiredError } from 'src/api/error';
import { ROUTES } from 'src/utils/constant';
import { type Course, type Mission } from 'src/utils/type';
import CourseCard from 'src/components/Card/CourseCard';
import MissionCard from 'src/components/Card/MissionCard';

import { ReactComponent as GreenBookIcon } from 'src/assets/green-book.svg';
import { ReactComponent as GreenStarIcon } from 'src/assets/green-star.svg';
import { ReactComponent as GreenPencilIcon } from 'src/assets/green-pencil.svg';
import { ReactComponent as BookIcon } from 'src/assets/book.svg';
import { ReactComponent as PencilIcon } from 'src/assets/pencil.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/calendar.svg';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';

const rolesMap = {
  'platform-admin': 'Platform Admin',
  'department-admin': 'Division Admin',
  trainee: 'Trainee',
};

export default function MainPage () {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useCurrentUser();
  const [currentTab, setCurrentTab] = useState('course');
  const [data, setData] = useState<{
    courses: Course[];
    missions: Mission[];
    results: Record<string, {
      done: number;
      total: number;
      hours: number;
      scores: number;
    }>;
  }>({
    courses: [],
    missions: [],
    results: {
      missions: {
        done: 0,
        total: 0,
        hours: 0,
        scores: 0,
      },
      courses: {
        done: 0,
        total: 0,
        hours: 0,
        scores: 0,
      },
      quizzes: {
        done: 0,
        total: 0,
        hours: 0,
        scores: 0,
      },
    },
  });
  const userId: string = user?.email ? user.email : '';
  const userRole: string = sessionStorage.getItem(CURRENT_USER_ROLE);
  const maxHours = Math.max(1, data.results.missions.hours, data.results.courses.hours, data.results.quizzes.hours);

  useEffect(() => {
    (async () => {
      let result;
      try {
        result = await getMyCourse();
        const courses = result.data.courses;
        setData(prev => ({ ...prev, courses }));

        result = await getMyMission();
        const missions = result.data.missions;
        setData(prev => ({ ...prev, missions }));

        result = await getMyResult();
        const results = result.data.results;
        setData(prev => ({ ...prev, results }));
      } catch (error) {
        if (error instanceof TokenExpiredError) {
          history.push(ROUTES.login);
        }
      }
    })();
  }, []);

  return (
    <PageLayout>
      <Grid container>
        <Grid item xs={12} sx={bannerHeader}>
          <div className="welcomeText">{t('Welcome to Online Training Class')}</div>
          <Box sx={bannerHeaderBox}>
            {userRole && <div className="userRole">{t(rolesMap[userRole])}</div>}
            <div>{userId}</div>
          </Box>
        </Grid>
        <Grid item xs={12} sx={bannerMain}>
          <img src={Banner} alt="banner-img" />
        </Grid>
      </Grid>

      <Box sx={content}>
        <Tabs
          value={currentTab}
          onChange={(event, newValue) => { setCurrentTab(newValue); }}
          TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
        >
          <Tab icon={<GreenBookIcon />} iconPosition={'start'} value="course" label={t('Your course')} />
          <Tab icon={<GreenStarIcon />} iconPosition={'start'} value="mission" label={t('Your mission')} />
          <Tab icon={<GreenPencilIcon />} iconPosition={'start'} value="result" label={t('Your result')} />
        </Tabs>
        <TabPanel value="course" currentTab={currentTab}>
          <div className="title">{t('Online training course')}</div>
          <Grid container spacing={'15px'}>
            {data.courses.map(course => (
              <Grid item xs={12} md={6} lg={3} key={course.courseid} sx={{ height: '100%' }}>
                <CourseCard course={course} simple />
              </Grid>
            ))}
          </Grid>
        </TabPanel>
        <TabPanel value="mission" currentTab={currentTab}>
          <div className="title">{t('Mission')}</div>
          <Grid container spacing={'15px'}>
            {data.missions.map(mission => (
              <Grid item xs={12} md={6} lg={3} key={mission.title.en} sx={{ height: '100%' }}>
                <MissionCard mission={mission} simple />
              </Grid>
            ))}
          </Grid>
        </TabPanel>
        <TabPanel value="result" currentTab={currentTab}>
          <Box
            sx={{
              display: 'flex',
              gap: '30px',
              flexWrap: 'wrap',
              '&>.MuiBox-root': {
                boxShadow: '0px 3px 6px #00000029',
              },
            }}
          >
            <Box width="700px" height="250px" display="flex" gap="50px" justifyContent="center" alignItems="center">
              <div>
                <div style={{ color: '#006EC2', fontSize: '24px', fontWeight: 'bold' }}>{t('Hi')}, {(user?.email ?? '').split('@')[0]}</div>
                <div style={{ color: '#707070', fontSize: '16px' }}>{t("Let's learn something new today")}</div>
                <Box sx={{ marginTop: '30px' }}>
                  <Box sx={{ textAlign: 'center', minWidth: '90px', display: 'inline-block' }}>
                    <BookIcon width="25px" height="25px" />
                    <div style={{ color: '#006EC2', fontSize: '18px', fontWeight: 'bold', marginTop: '15px' }}>
                      {t('Course')}
                    </div>
                    <NumberProgress current={data.results.courses.done} total={data.results.courses.total} />
                  </Box>
                  <Box sx={{ textAlign: 'center', minWidth: '90px', display: 'inline-block', marginLeft: '30px' }}>
                    <CalendarIcon width="25px" height="25px" />
                    <div style={{ color: '#006EC2', fontSize: '18px', fontWeight: 'bold', marginTop: '15px' }}>
                      {t('Quiz')}
                    </div>
                    <NumberProgress current={data.results.quizzes.done} total={data.results.quizzes.total} />
                  </Box>
                  <Box sx={{ textAlign: 'center', minWidth: '90px', display: 'inline-block', marginLeft: '30px' }}>
                    <PencilIcon width="25px" height="25px" />
                    <div style={{ color: '#006EC2', fontSize: '18px', fontWeight: 'bold', marginTop: '15px' }}>
                      {t('Mission')}
                    </div>
                    <NumberProgress current={data.results.missions.done} total={data.results.missions.total} />
                  </Box>
                </Box>
              </div>
              <Box width="150px" height="150px">
                <CirclePercentage
                  current={data.results.courses.done + data.results.quizzes.done + data.results.missions.done}
                  total={data.results.courses.total + data.results.quizzes.total + data.results.missions.total}
                  upperText={t('Completed rate')}
                  lowerText="%"
                />
              </Box>
            </Box>
            <Box width="250px" height="250px" padding="20px">
              <div style={{ color: '#006EC2', fontSize: '18px', fontWeight: 'bold' }}>{t('Hour spent')}</div>
              <Box marginTop="20px">
                {[
                  { label: t('Course'), key: 'courses' },
                  { label: t('Quiz'), key: 'quizzes' },
                  { label: t('Mission'), key: 'missions' },
                ].map(item => (
                  <Box key={item.key} height="40px" display="flex" flexWrap="nowrap" alignItems="center">
                    <Box
                      display="inline-block"
                      color="#006EC2"
                      fontSize="12px"
                      height="40px"
                      lineHeight="40px"
                      width="55px"
                      borderRight="2px solid #006EC2"
                      paddingRight="10px"
                      textAlign="right"
                    >
                      {item.label}
                    </Box>
                    <Box
                      height="20px"
                      width={(100 / maxHours) * data.results?.[item.key].hours + 'px'}
                      sx={{ backgroundColor: '#57A6E8' }}
                    ></Box>
                    <Box fontSize="12px" marginLeft="3px" color="#006EC2">
                      {data.results?.[item.key].hours.toFixed(1)} {t('hr')}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box color="#707070" textAlign="center" marginTop="20px">
                {t('Weekly')}
              </Box>
            </Box>
            <Box width="400px" height="250px" padding="20px 25px">
              <div style={{ color: '#006EC2', fontSize: '18px', fontWeight: 'bold' }}>{t('Performance')}</div>
              <Box marginTop="20px" display="flex" gap="40px" alignItems="center" justifyContent="center">
                <Box width="150px" height="150px">
                  <CirclePercentage
                    current={data.results.quizzes.scores / 100 + data.results.missions.scores / 100}
                    total={data.results.quizzes.done + data.results.missions.done}
                    upperText={t('Average score')}
                    lowerText=""
                  />
                </Box>
                <Box>
                  <div style={{ color: '#006EC2', fontSize: '12px' }}>
                    {t('Quiz')}: {data.results.quizzes.scores} {t('scores')}
                  </div>
                  <div style={{ color: '#006EC2', fontSize: '12px' }}>
                    {t('Mission')}: {data.results.missions.scores} {t('scores')}
                  </div>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </Box>
    </PageLayout>
  );
}

const CirclePercentage = ({
  current,
  total,
  upperText,
  lowerText,
}: {
  current: number;
  total: number;
  upperText: string;
  lowerText: string;
}) => {
  let percentage = 0;
  if (total) {
    percentage = (current / total) * 100;
  }

  return (
    <CircularProgressbarWithChildren
      value={percentage}
      styles={buildStyles({
        pathColor: '#80B6E0',
        backgroundColor: '#80B6E0',
      })}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#006EC2' }}>
        <span style={{ fontSize: '16px' }}>{upperText}</span>
        <strong style={{ fontSize: '24px' }}>{percentage.toFixed(1) + lowerText}</strong>
      </div>
    </CircularProgressbarWithChildren>
  );
};

const NumberProgress = ({ current, total }: { current: number; total: number; }) => {
  return (
    <div
      style={{
        marginTop: '10px',
        width: '90px',
        height: '40px',
        lineHeight: '40px',
        fontSize: '24px',
        color: '#006EC2',
        backgroundColor: '#E9F5FF',
        borderRadius: '2px',
        boxShadow: 'inset 0px 0px 3px #57A6E8',
      }}
    >
      {current}&nbsp;/&nbsp;{total}
    </div>
  );
};

const TabPanel = ({
  children,
  value,
  currentTab,
}: {
  children: React.ReactNode;
  value: string;
  currentTab: string;
}) => {
  return (
    <div className="tab-panel" hidden={value !== currentTab}>
      {children}
    </div>
  );
};
